import SIGNATURE_B64 from "./signature";
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import "./VerifyCertificate.css";

const API = process.env.REACT_APP_API_URL ||
  "https://porfolio-backend-production-aab2.up.railway.app/api";

export default function VerifyCertificate() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialId = searchParams.get("id") || "";
  const [certId, setCertId] = useState(initialId);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialId) handleVerify(initialId);
    // eslint-disable-next-line
  }, []);

  async function handleVerify(id = certId) {
    if (!id.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.get(`${API}/internships/verify/${id.trim().toUpperCase()}`);
      setResult({ found: true, data: res.data });
    } catch {
      setResult({ found: false });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="verify-page">
      <div className="verify-container">
        <div className="verify-header">
          <span className="verify-badge">Certificate Verification</span>
          <h1>Verify Your Certificate</h1>
          <p>Enter your Certificate ID to confirm authenticity.<br />
            Format: <code>ATS-FSD-2026-001</code></p>
        </div>

        <div className="verify-search-box">
          <input
            type="text" className="verify-input"
            placeholder="e.g. ATS-FSD-2026-001"
            value={certId}
            onChange={(e) => setCertId(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleVerify()}
          />
          <button className="verify-btn" onClick={() => handleVerify()} disabled={loading || !certId.trim()}>
            {loading ? "Checking..." : "Verify →"}
          </button>
        </div>

        {result && (
          <div className={`verify-result ${result.found ? "verify-result--found" : "verify-result--notfound"}`}>
            {result.found ? <VerifiedCard data={result.data} /> : <NotFound id={certId} />}
          </div>
        )}

        <div className="verify-footer">
          <span onClick={() => navigate("/internship")} className="verify-link">← Back to Internship Program</span>
        </div>
      </div>
    </div>
  );
}

function VerifiedCard({ data }) {
  const verifyUrl = `https://ariyomtechsolutions.site/verify?id=${data.certificateId}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(verifyUrl)}`;

  async function loadImage(src) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
  }

  async function downloadCertificate() {
    // Date formatting helper
    const formatDate = (dateValue) => {
      if (!dateValue) return null;
      // If already a readable string like "17 April 2026", return as-is
      if (isNaN(Date.parse(dateValue))) return dateValue;
      // Force local timezone by appending T12:00:00
      const date = new Date(
        typeof dateValue === "string" && dateValue.length === 10
          ? dateValue + "T12:00:00"
          : dateValue
      );
      if (isNaN(date.getTime())) return dateValue;
      return date.toLocaleDateString("en-IN", {
        day: "numeric", month: "long", year: "numeric",
      });
    };

    // Get all dynamic values with fallbacks
    const startDate = formatDate(data.startDate) || "17 April 2026";
    const endDate = formatDate(data.endDate) || "17 May 2026";
    const issuedDate = formatDate(data.issuedDate || data.endDate) || endDate;
    
    // Debug logging
    console.log("Certificate data:", data);
    console.log("startDate:", data.startDate, "→", startDate);
    console.log("endDate:", data.endDate, "→", endDate);
    console.log("issuedDate:", data.issuedDate, "→", issuedDate);
    
    const fullName = (data.fullName || "Student Name").toUpperCase();
    const domain = data.domain || "Full Stack Development";
    const certificateId = data.certificateId || "ATS-FSD-2026-001";
    const verifyUrlText = `ariyomtechsolutions.site/verify?id=${certificateId}`;

    const W = 1400, H = 990;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    // White background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, H);

    // Outer blue border
    ctx.strokeStyle = "#1a3c8f"; ctx.lineWidth = 14;
    ctx.strokeRect(7, 7, W - 14, H - 14);

    // Inner thin border
    ctx.strokeStyle = "#1a3c8f"; ctx.lineWidth = 3;
    ctx.strokeRect(28, 28, W - 56, H - 56);

    // Corner L-brackets
    [[48,48,1,1],[W-48,48,-1,1],[48,H-48,1,-1],[W-48,H-48,-1,-1]].forEach(([x,y,fx,fy]) => {
      ctx.strokeStyle = "#1a3c8f"; ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(x + fx * 40, y); ctx.lineTo(x, y); ctx.lineTo(x, y + fy * 40);
      ctx.stroke();
    });

    // Top blue bar
    ctx.fillStyle = "#1a3c8f";
    ctx.fillRect(28, 28, W - 56, 7);

    // Company name top left
    ctx.textAlign = "left";
    ctx.fillStyle = "#1a3c8f"; ctx.font = "bold 36px Georgia, serif";
    ctx.fillText("Ariyom ", 70, 112);
    const aw = ctx.measureText("Ariyom ").width;
    ctx.fillStyle = "#0099cc";
    ctx.fillText("Tech ", 70 + aw, 112);
    const tw = ctx.measureText("Tech ").width;
    ctx.fillStyle = "#1a3c8f";
    ctx.fillText("Solutions", 70 + aw + tw, 112);
    ctx.fillStyle = "#999"; ctx.font = "12px Georgia, serif";
    ctx.fillText("SKILL DEVELOPMENT & FUTURE TECH", 70, 136);

    // MSME top right (placeholder for logo + text)
    // Logo would be drawn at x=1110, y=55, width=130, height=90 if available
    ctx.textAlign = "right";
    ctx.fillStyle = "#1a3c8f"; ctx.font = "bold 15px Georgia, serif";
    ctx.fillText("MSME Udyam Registered Enterprise", W - 70, 120);
    ctx.fillStyle = "#888"; ctx.font = "13px Georgia, serif";
    ctx.fillText("ariyomtechsolutions.site", W - 70, 142);

    // Header divider
    ctx.strokeStyle = "#dde3f0"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(70, 170); ctx.lineTo(W - 70, 170); ctx.stroke();

    // Certificate Title
    ctx.fillStyle = "#1a3c8f"; ctx.font = "bold 70px Georgia, serif"; ctx.textAlign = "center";
    ctx.fillText("Certificate", W / 2, 280);
    
    // Subtitle
    ctx.fillStyle = "#1a3c8f"; ctx.font = "18px Georgia, serif";
    ctx.fillText("OF INTERNSHIP COMPLETION", W / 2, 325);

    // Decorative line with diamond
    ctx.strokeStyle = "#1a3c8f"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(W/2 - 120, 345); ctx.lineTo(W/2 + 120, 345); ctx.stroke();
    ctx.fillStyle = "#1a3c8f";
    ctx.fillRect(W/2 - 5, 340, 10, 10);

    // Subtitle text
    ctx.fillStyle = "#555"; ctx.font = "italic 22px Georgia, serif";
    ctx.fillText("This acknowledgment is proudly presented to", W / 2, 410);

    // Student name - uppercase with underline
    ctx.fillStyle = "#111"; ctx.font = "bold 60px Georgia, serif";
    ctx.fillText(fullName, W / 2, 500);
    const nw = ctx.measureText(fullName).width;
    ctx.strokeStyle = "#1a3c8f"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(W/2-nw/2, 518); ctx.lineTo(W/2+nw/2, 518); ctx.stroke();

    // Body text - clean 2-line paragraph
    ctx.font = "22px Georgia, serif"; ctx.fillStyle = "#333";
    ctx.fillText("This is to certify that " + fullName + " has successfully completed a 30-Day Project-Based Internship", W / 2, 610);
    ctx.fillText("in " + domain + " from " + startDate + " to " + endDate + ".", W / 2, 650);

    // Issued date
    ctx.fillStyle = "#1a3c8f"; ctx.font = "22px Georgia, serif";
    ctx.fillText("Issued on: " + issuedDate, W / 2, 710);

    // Separator line
    ctx.strokeStyle = "#1a3c8f"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(60, 760); ctx.lineTo(W - 60, 760); ctx.stroke();

    // Generate QR locally (no external URL — no canvas taint)
    const qrDataUrl = await QRCode.toDataURL(verifyUrl, {
      width: 140,
      margin: 1,
      color: { dark: "#1a3c8f", light: "#ffffff" },
    });

    // Load QR and signature in parallel
    const [qrImg, sigImg] = await Promise.all([
      loadImage(qrDataUrl),
      loadImage(`data:image/png;base64,${SIGNATURE_B64}`),
    ]);

    // QR Verification Section - bottom left
    if (qrImg) ctx.drawImage(qrImg, 80, 790, 140, 140);
    
    ctx.fillStyle = "#1a3c8f";
    ctx.font = "bold 14px Georgia, serif";
    ctx.textAlign = "left";
    ctx.fillText("VERIFIED CERTIFICATE", 240, 820);

    ctx.fillStyle = "#1a3c8f";
    ctx.font = "13px Arial, sans-serif";
    ctx.fillText("ID: " + certificateId, 240, 845);

    ctx.fillStyle = "#0099cc";
    ctx.font = "11px Arial, sans-serif";
    ctx.fillText("Verify at:", 240, 870);
    ctx.fillText(verifyUrlText, 240, 888);

    // Signature bottom right — transparent PNG
    if (sigImg) {
      ctx.globalAlpha = 0.85;
      ctx.drawImage(sigImg, 920, 755, 380, 115);
      ctx.globalAlpha = 1.0;
    }

    // Signature line
    ctx.strokeStyle = "#333"; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(940, 860); ctx.lineTo(1260, 860); ctx.stroke();

    // Founder designation below signature
    ctx.fillStyle = "#1a3c8f"; ctx.font = "13px Georgia, serif"; ctx.textAlign = "center";
    ctx.fillText("Founder, Ariyom Tech Solutions", 1100, 885);

    // Bottom blue bar
    ctx.fillStyle = "#1a3c8f";
    ctx.fillRect(28, H - 35, W - 56, 7);

    // Download
    const link = document.createElement("a");
    link.download = `${certificateId}-Certificate.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }

  return (
    <div className="vc-card">
      <div className="vc-status-row">
        <span className="vc-verified-badge">✓ Verified</span>
        <span className={`vc-status-pill ${data.status === "Valid" ? "vc-valid" : "vc-revoked"}`}>{data.status}</span>
      </div>
      <h2 className="vc-name">{data.fullName}</h2>
      <p className="vc-domain">{data.domain} Internship<br />Conducted by Ariyom Tech Solutions</p>
      <div className="vc-details-grid">
        <Detail label="Certificate ID" value={data.certificateId} mono />
        <Detail label="Domain" value={data.domain} />
        <Detail label="Issue Date" value={formatDate(data.issuedAt)} />
        <Detail label="College" value={data.collegeName} />
        {data.linkedinProfile && <Detail label="LinkedIn" value={<a href={data.linkedinProfile} target="_blank" rel="noreferrer" className="vc-link">View →</a>} />}
        {data.githubProfile && <Detail label="GitHub" value={<a href={data.githubProfile} target="_blank" rel="noreferrer" className="vc-link">View →</a>} />}
      </div>
      {data.status === "Valid" && (
        <div className="vc-qr-section">
          <img src={qrUrl} alt="Verify QR" className="vc-qr-img" />
          <div>
            <p className="vc-qr-label">Scan to verify this certificate</p>
            <p className="vc-qr-url">{verifyUrl}</p>
          </div>
        </div>
      )}
      {data.status === "Valid" && (
        <button className="vc-download-btn" onClick={downloadCertificate}>
          ⬇ Download Certificate
        </button>
      )}
      <div className="vc-footer-note">MSME Udyam Registered Enterprise · ariyomtechsolutions.site</div>
    </div>
  );
}

function NotFound({ id }) {
  return (
    <div className="vc-notfound">
      <div className="vc-nf-icon">❌</div>
      <h3>Certificate Not Found</h3>
      <p>No certificate found for ID <strong>{id}</strong>.<br />Contact support@ariyomtechsolutions.site</p>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div className="vc-detail">
      <span className="vc-detail-label">{label}</span>
      <span className={`vc-detail-value${mono ? " vc-mono" : ""}`}>{value}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
}